import React from 'react';
import Loadable from 'react-loadable';
import { enablePreloadingContainer } from '../../../constants/globals';

// Keep original loading behavior - no visible loading UI
const loading = () => <React.Fragment />;

/**
 * Enhanced asyncRoute with selective preloading support
 * @param {Function} getComponent - Component loader function
 * @param {boolean} shouldPreload - Whether to preload this component immediately
 * @param {Object} options - Additional options for Loadable
 * @returns {Component} Loadable component
 */
const asyncRoute = (getComponent, shouldPreload = false, options = {}) => {
  const component = Loadable({
    loader: getComponent,
    loading,
    ...options,
  });

  const preloadComponent = () => {
    // preload component when critical loading is completed
    return new Promise((resolve, reject) => {
          const doPreload = () => {
              try {
                const maybePromise = component.preload();
                if (maybePromise && typeof maybePromise.then === 'function') {
                  maybePromise.then(resolve).catch(reject);
                } else {
                  resolve();
                }
              } catch (error) {
                reject(error);
              }
            };
        
            if (typeof window !== 'undefined' && document.readyState === 'complete') {
              doPreload();
            } else {
              window.addEventListener('load', doPreload, { once: true });
            }
          });
  };

  // Only preload critical components to improve initial bundle size
  // This prevents loading all components at startup, improving Lighthouse scores
  const cascadeActiveCount =
    typeof window !== 'undefined' ? window.__ASYNC_ROUTE_CASCADE_ACTIVE__ : 0;
  const allowNestedPreload = options && options.allowNestedPreload;

  const startCascade = shouldPreload && allowNestedPreload;
  if (startCascade) {
    // Enable cascading so that any asyncRoute created during this module's evaluation
    // will also be preloaded automatically.
    if (typeof window !== 'undefined') {
      window.__ASYNC_ROUTE_CASCADE_ACTIVE__ = (window.__ASYNC_ROUTE_CASCADE_ACTIVE__ || 0) + 1;
      if (!window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__) {
        window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__ = [];
      }
      window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__.push(
        window.__ASYNC_ROUTE_CASCADE_ALLOWED__ || undefined,
      );
      // No allow-list gating: cascade purely by props
      delete window.__ASYNC_ROUTE_CASCADE_ALLOWED__;
    }

    const done = () => {
      if (typeof window !== 'undefined') {
        const current = window.__ASYNC_ROUTE_CASCADE_ACTIVE__ || 1;
        window.__ASYNC_ROUTE_CASCADE_ACTIVE__ = Math.max(current - 1, 0);
        if (
          window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__ &&
          window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__.length
        ) {
          const prev = window.__ASYNC_ROUTE_CASCADE_ALLOWED_STACK__.pop();
          if (prev === undefined) {
            delete window.__ASYNC_ROUTE_CASCADE_ALLOWED__;
          } else {
            window.__ASYNC_ROUTE_CASCADE_ALLOWED__ = prev;
          }
        }
      }
    };

    try {
      const maybePromise = preloadComponent();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(done).catch(done);
      } else {
        done();
      }
    } catch (e) {
      done();
    }
  } else if (cascadeActiveCount > 0) {
    // Preload during active cascade regardless of child props
    preloadComponent();
  } else if (shouldPreload || enablePreloadingContainer) {
    component.preload();
  }

  return component;
};

export default asyncRoute;
